/robloky/**
 * Image Compression & Optimization Utility for Robloky Gaming
 * Automatically resizes, centers/crops square avatars, and compresses
 * photos uploaded from phones or cameras to lightweight WebP/JPEG formats.
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0.1 to 1.0
  cropSquare?: boolean; // Perfect 1:1 square crop for avatars
  format?: 'image/webp' | 'image/jpeg' | 'image/png';
}

/**
 * Compresses an image file (e.g., from an `<input type="file">` on mobile/desktop)
 * and returns an optimized Base64 Data URL.
 */
export const compressImageFile = async (
  file: File | Blob,
  options: CompressionOptions = {}
): Promise<{ dataUrl: string; originalSize: number; compressedSize: number; width: number; height: number }> => {
  const {
    maxWidth = 360,
    maxHeight = 360,
    quality = 0.82,
    cropSquare = true,
    format = 'image/webp',
  } = options;

  const originalSize = file.size;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => reject(new Error('Failed to read image file from device.'));

    reader.onload = (e) => {
      const src = e.target?.result as string;
      if (!src) {
        reject(new Error('Empty image file data.'));
        return;
      }

      const img = new Image();
      img.onerror = () => reject(new Error('Failed to decode image. Format may be unsupported.'));

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d', { alpha: format === 'image/png' });

          if (!ctx) {
            reject(new Error('Canvas 2D context is not supported.'));
            return;
          }

          let srcX = 0;
          let srcY = 0;
          let srcWidth = img.width;
          let srcHeight = img.height;
          let targetWidth = img.width;
          let targetHeight = img.height;

          if (cropSquare) {
            // Calculate center square crop
            const minDim = Math.min(srcWidth, srcHeight);
            srcX = (srcWidth - minDim) / 2;
            srcY = (srcHeight - minDim) / 2;
            srcWidth = minDim;
            srcHeight = minDim;

            const finalDim = Math.min(minDim, maxWidth);
            targetWidth = finalDim;
            targetHeight = finalDim;
          } else {
            // Maintain aspect ratio with maxWidth / maxHeight
            if (targetWidth > maxWidth || targetHeight > maxHeight) {
              const ratio = Math.min(maxWidth / targetWidth, maxHeight / targetHeight);
              targetWidth = Math.round(targetWidth * ratio);
              targetHeight = Math.round(targetHeight * ratio);
            }
          }

          canvas.width = targetWidth;
          canvas.height = targetHeight;

          // Enable high quality image smoothing
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          // Draw cropped/scaled image onto canvas
          ctx.drawImage(
            img,
            srcX,
            srcY,
            srcWidth,
            srcHeight,
            0,
            0,
            targetWidth,
            targetHeight
          );

          // Attempt WebP export, fallback to JPEG if unsupported
          let finalFormat = format;
          let compressedDataUrl = '';

          try {
            compressedDataUrl = canvas.toDataURL(finalFormat, quality);
            // Verify that browser supports WebP canvas export (some safari versions fallback to png data)
            if (finalFormat === 'image/webp' && !compressedDataUrl.startsWith('data:image/webp')) {
              compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
              finalFormat = 'image/jpeg';
            }
          } catch {
            compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
            finalFormat = 'image/jpeg';
          }

          // Calculate approximate byte size from Base64 string
          const stringLength = compressedDataUrl.length - 'data:image/webp;base64,'.length;
          const compressedSize = Math.round((stringLength * 3) / 4);

          resolve({
            dataUrl: compressedDataUrl,
            originalSize,
            compressedSize,
            width: targetWidth,
            height: targetHeight,
          });
        } catch (err) {
          reject(err);
        }
      };

      img.src = src;
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Formats bytes into a clean human-readable string (e.g., "45.2 KB", "1.2 MB")
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
};
